'use client'

// Google Maps Embed Component - Based on working /map-test implementation
// Uses Google Maps "Share > Embed" method (pb= parameter) - NO API key required

interface GoogleMapEmbedProps {
  embedUrl: string
  title: string
  lat: number
  lng: number
}

export default function GoogleMapEmbed({ embedUrl, title, lat, lng }: GoogleMapEmbedProps) {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      padding: '16px',
      background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '20px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
      border: '1px solid rgba(255, 255, 255, 0.8)'
    }}>
      {/* Decorative corner accents */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        width: '40px',
        height: '40px',
        borderTop: '3px solid #0e1b16',
        borderLeft: '3px solid #0e1b16',
        borderRadius: '20px 0 0 0',
        opacity: 0.15
      }} />
      <div style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        width: '40px',
        height: '40px',
        borderTop: '3px solid #0e1b16',
        borderRight: '3px solid #0e1b16',
        borderRadius: '0 20px 0 0',
        opacity: 0.15
      }} />
      <div style={{
        position: 'absolute',
        bottom: '12px',
        left: '12px',
        width: '40px',
        height: '40px',
        borderBottom: '3px solid #0e1b16',
        borderLeft: '3px solid #0e1b16',
        borderRadius: '0 0 0 20px',
        opacity: 0.15
      }} />
      <div style={{
        position: 'absolute',
        bottom: '12px',
        right: '12px',
        width: '40px',
        height: '40px',
        borderBottom: '3px solid #0e1b16',
        borderRight: '3px solid #0e1b16',
        borderRadius: '0 0 20px 0',
        opacity: 0.15
      }} />

      <div style={{
        position: 'relative',
        width: '100%',
        height: '450px',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(0, 0, 0, 0.05)',
        border: '2px solid #fff'
      }}>
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={title}
        />
      </div>
    </div>
  )
}

// Pre-generated Google Maps embed URLs (from Google Maps Share > Embed)
// Using !5e1 for satellite imagery view (instead of !5e0 for street map)
export const GOOGLE_MAPS_EMBEDS = {
  'la-parva': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13295.544673895788!2d-70.29241425!3d-33.3454193!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662c9b3c3c9c8e7%3A0x8e5a8c8e5a8c8e5a!2sLa%20Parva!5e1!3m2!1sen!2scl!4v1705680000000!5m2!1sen!2scl',
    title: 'La Parva Ski Resort, Chile',
    lat: -33.3447,
    lng: -70.2847
  },
  'tres-valles': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53450.0!2d-70.27!3d-33.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDIxJzAwLjAiUyA3MMKwMTYnMTIuMCJX!5e1!3m2!1sen!2scl!4v1737326000001',
    title: 'Tres Valles Ski Area, Chile',
    lat: -33.35,
    lng: -70.27
  },
  'yelcho': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95000.0!2d-72.35!3d-43.23!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDEzJzQ4LjAiUyA3MsKwMjEnMDAuMCJX!5e1!3m2!1sen!2scl!4v1737326000002',
    title: 'Yelcho Lake, Patagonia, Chile',
    lat: -43.23,
    lng: -72.35
  },
  'chamonix': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44350.0!2d6.8694!3d45.9237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDU1JzI1LjMiTiA2wrogNTInMDkuOCJF!5e1!3m2!1sen!2sfr!4v1737326000003',
    title: 'Chamonix-Mont-Blanc, French Alps',
    lat: 45.9237,
    lng: 6.8694
  },
  'crested-butte': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d50000.0!2d-106.9878!3d38.8697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDUyJzEwLjkiTiAxMDbCsDU5JzE2LjEiVw!5e1!3m2!1sen!2sus!4v1737326000004',
    title: 'Crested Butte, Colorado, USA',
    lat: 38.8697,
    lng: -106.9878
  },
  'villarrica': {
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d80000.0!2d-71.9517!3d-39.2851!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDE3JzA2LjQiUyA3McKwNTcnMDYuMSJX!5e1!3m2!1sen!2scl!4v1737326000005',
    title: 'Lake Villarrica, Chile',
    lat: -39.2851,
    lng: -71.9517
  }
}
